import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, GraduationCap, UserPlus, Activity, TrendingUp,
  CheckCircle2, Clock, PauseCircle, XCircle, ArrowUpRight,
  IndianRupee, CalendarClock
} from 'lucide-react';
import api from '../../utils/api';
import './platform.css';

const STATUS_META = {
  active:    { label: 'Active',    icon: CheckCircle2, tone: 'ok' },
  trialing:  { label: 'Trialing',  icon: Clock,        tone: 'warn' },
  paused:    { label: 'Paused',    icon: PauseCircle,  tone: 'muted' },
  cancelled: { label: 'Cancelled', icon: XCircle,      tone: 'danger' },
};

const PLAN_META = {
  free:       { label: 'Free',       color: '#8a7560' },
  basic:      { label: 'Basic',      color: '#3b6b2e' },
  premium:    { label: 'Premium',    color: '#b66a12' },
  enterprise: { label: 'Enterprise', color: '#4b2a6b' },
};

const PlatformDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/platform/stats');
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.message || 'Could not load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="pf-loading"><div className="pf-spinner" /> Loading platform data…</div>;
  }
  if (err) {
    return <div className="pf-error">{err}</div>;
  }

  const t = stats.totals;
  const s = stats.status;
  const plans = stats.byPlan || [];
  const rev = stats.revenue || { mrr: 0, arr: 0 };

  return (
    <div className="pf-page">
      <header className="pf-page-head">
        <div>
          <div className="pf-eyebrow">Platform</div>
          <h1 className="pf-title">Overview</h1>
          <p className="pf-sub">Every tenant on EduManage at a glance.</p>
        </div>
        <button className="pf-cta" onClick={() => navigate('/platform/schools')}>
          Open schools <ArrowUpRight size={15} />
        </button>
      </header>

      {/* Revenue tiles — headline numbers first. */}
      <div className="pf-tiles">
        <MoneyTile icon={IndianRupee}    label="Monthly recurring (MRR)" value={rev.mrr} accent="#8a540a" />
        <MoneyTile icon={CalendarClock}  label="Annual run-rate (ARR)"   value={rev.arr} accent="#4b2a6b" />
        <StatTile  icon={Building2}      label="Schools"  value={t.schools}  accent="#b66a12" />
        <StatTile  icon={GraduationCap}  label="Students" value={t.students} accent="#3b6b2e" />
      </div>

      {/* Secondary counts */}
      <div className="pf-tiles">
        <StatTile icon={Users}    label="Teachers" value={t.teachers} accent="#1f567d" />
        <StatTile icon={UserPlus} label="Parents"  value={t.parents}  accent="#8a2c5e" />
      </div>

      {/* Secondary row */}
      <div className="pf-row-2">
        {/* Status breakdown */}
        <div className="pf-card">
          <div className="pf-card-head">
            <Activity size={15} />
            <span>Tenant status</span>
          </div>
          <div className="pf-status-grid">
            {Object.entries(STATUS_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <div key={key} className={`pf-status-chip tone-${meta.tone}`}>
                  <Icon size={15} />
                  <div>
                    <div className="pf-status-count">{s[key] ?? 0}</div>
                    <div className="pf-status-label">{meta.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Plan distribution */}
        <div className="pf-card">
          <div className="pf-card-head">
            <TrendingUp size={15} />
            <span>Plan mix</span>
          </div>
          <div className="pf-plans">
            {plans.length === 0 ? (
              <div className="pf-empty">No tenants yet.</div>
            ) : plans.map(p => {
              const meta = PLAN_META[p.plan] || { label: p.plan, color: '#8a7560' };
              const pct = t.schools > 0 ? Math.round((p.count / t.schools) * 100) : 0;
              return (
                <div key={p.plan} className="pf-plan-row">
                  <div className="pf-plan-top">
                    <span className="pf-plan-label">
                      <span className="pf-plan-dot" style={{ background: meta.color }} />
                      {meta.label}
                    </span>
                    <span className="pf-plan-count">{p.count} · {pct}%</span>
                  </div>
                  <div className="pf-bar"><div className="pf-bar-fill" style={{ width: `${pct}%`, background: meta.color }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Growth */}
      <div className="pf-card">
        <div className="pf-card-head">
          <TrendingUp size={15} />
          <span>Signups · last 30 days</span>
        </div>
        <GrowthBars signups={stats.signupsLast30Days || []} />
      </div>
    </div>
  );
};

const StatTile = ({ icon: Icon, label, value, accent }) => (
  <div className="pf-tile">
    <span className="pf-tile-ic" style={{ color: accent, background: accent + '14' }}>
      <Icon size={18} />
    </span>
    <div>
      <div className="pf-tile-value">{Number(value).toLocaleString('en-IN')}</div>
      <div className="pf-tile-label">{label}</div>
    </div>
  </div>
);

// Compact INR formatter: 12,00,000 → "₹12L", 30,000 → "₹30K".
const formatINR = (n) => {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1)}Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)}L`;
  if (num >= 1000)     return `₹${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const MoneyTile = ({ icon: Icon, label, value, accent }) => (
  <div className="pf-tile pf-tile-money">
    <span className="pf-tile-ic" style={{ color: accent, background: accent + '14' }}>
      <Icon size={18} />
    </span>
    <div>
      <div className="pf-tile-value">{formatINR(value)}</div>
      <div className="pf-tile-label">{label}</div>
    </div>
  </div>
);

const GrowthBars = ({ signups }) => {
  // Fill missing days so a sparse 30-day window still shows gaps, not just peaks.
  const now = new Date();
  const days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (29 - i));
    const key = d.toISOString().slice(0, 10);
    const row = signups.find((r) => String(r.day).slice(0, 10) === key);
    return { date: key, count: row ? Number(row.count) : 0 };
  });
  const max = Math.max(1, ...days.map((d) => d.count));

  return (
    <div className="pf-growth">
      {days.map((d, i) => (
        <div
          key={d.date}
          className="pf-growth-bar"
          title={`${d.date}: ${d.count} signup${d.count === 1 ? '' : 's'}`}
        >
          <div
            className="pf-growth-fill"
            style={{ height: `${(d.count / max) * 100}%`, opacity: d.count === 0 ? 0.18 : 1 }}
          />
          {i % 5 === 0 && <span className="pf-growth-tick">{d.date.slice(5)}</span>}
        </div>
      ))}
    </div>
  );
};

export default PlatformDashboard;
